import PropTypes from "prop-types";
import React, { lazy, Suspense, useEffect } from "react";

import ErrorBoundary from "~/components/ErrorBoundary";
import blockNameTransform from "~/utilities/blockNameTransform";

const AcfAccordionsBlock = lazy(() => import("~/blocks/acf/AccordionsBlock"));
const AcfAdBlock = lazy(() => import("~/blocks/acf/AdBlock"));
const AcfAdCardsGroupBlock = lazy(
    () => import("~/blocks/acf/AdCardsGroupBlock"),
);
const AcfAllApplicationsBlock = lazy(
    () => import("~/blocks/acf/AllApplicationsBlock"),
);
const AcfAllCaseStudiesBlock = lazy(
    () => import("~/blocks/acf/AllCaseStudiesBlock"),
);
const AcfAllEventsBlock = lazy(() => import("~/blocks/acf/AllEventsBlock"));
const AcfAllFaqsBlock = lazy(() => import("~/blocks/acf/AllFaqsBlock"));
const AcfAllInsightsBlock = lazy(() => import("~/blocks/acf/AllInsightsBlock"));
const AcfAllNewsBlock = lazy(() => import("~/blocks/acf/AllNewsBlock"));
const AcfCalculatorBlock = lazy(() => import("~/blocks/acf/CalculatorBlock"));
const AcfCardLinksBlock = lazy(() => import("~/blocks/acf/CardLinksBlock"));
const AcfContactBlock = lazy(() => import("~/blocks/acf/ContactBlock"));
const AcfDocumentLibraryBlock = lazy(
    () => import("~/blocks/acf/DocumentLibraryBlock"),
);
const AcfDocumentsBlock = lazy(() => import("~/blocks/acf/DocumentsBlock"));
const AcfFaqsBlock = lazy(() => import("~/blocks/acf/FaqsBlock"));
const AcfFeaturesBlock = lazy(() => import("~/blocks/acf/FeaturesBlock"));
const AcfFilterAdBlock = lazy(() => import("~/blocks/acf/FilterAdBlock"));
const AcfHeroBlock = lazy(() => import("~/blocks/acf/HeroBlock"));
const AcfHeroCardsBlock = lazy(() => import("~/blocks/acf/HeroCardsBlock"));
const AcfHeroLogoButtonsBlock = lazy(
    () => import("~/blocks/acf/HeroLogoButtonsBlock"),
);
const AcfHubspotFormBlock = lazy(() => import("~/blocks/acf/HubspotFormBlock"));
const AcfIconLinksBlock = lazy(() => import("~/blocks/acf/IconLinksBlock"));
const AcfImageCardsBlock = lazy(() => import("~/blocks/acf/ImageCardsBlock"));
const AcfIframeEmbedBlock = lazy(() => import("~/blocks/acf/IframeEmbedBlock"));
const AcfImageTextBlock = lazy(() => import("~/blocks/acf/ImageTextBlock"));
const AcfInformationBlock = lazy(() => import("~/blocks/acf/InformationBlock"));
const AcfInsightsBlock = lazy(() => import("~/blocks/acf/InsightsBlock"));
const AcfJsEmbedBlock = lazy(() => import("~/blocks/acf/JsEmbedBlock"));
const AcfJumpLinksNavigationBlock = lazy(
    () => import("~/blocks/acf/JumpLinksNavigationBlock"),
);
const AcfLinksAdBlock = lazy(() => import("~/blocks/acf/LinksAdBlock"));
const AcfLogosBlock = lazy(() => import("~/blocks/acf/LogosBlock"));
const AcfPageHeaderBlock = lazy(() => import("~/blocks/acf/PageHeaderBlock"));
const AcfProductsCarouselBlock = lazy(
    () => import("~/blocks/acf/ProductsCarouselBlock"),
);
const AcfProductSupportBlock = lazy(
    () => import("~/blocks/acf/ProductSupportBlock"),
);
const AcfQrScannerBlock = lazy(() => import("~/blocks/acf/QrScannerBlock"));
const AcfQuoteBlock = lazy(() => import("~/blocks/acf/QuoteBlock"));
const AcfQuoteSliderBlock = lazy(() => import("~/blocks/acf/QuoteSliderBlock"));
const AcfRmaFormBlock = lazy(() => import("~/blocks/acf/RmaFormBlock"));
const AcfSearchResultsBlock = lazy(
    () => import("~/blocks/acf/SearchResultsBlock"),
);
const AcfSiteSearchBlock = lazy(() => import("~/blocks/acf/SiteSearchBlock"));
const AcfTimelineBlock = lazy(() => import("~/blocks/acf/TimelineBlock"));
const AcfVideoEmbedBlock = lazy(() => import("~/blocks/acf/VideoEmbedBlock"));
const AcfVideoLinksBlock = lazy(() => import("~/blocks/acf/VideoLinksBlock"));
const AcfWhereToBuyBlock = lazy(() => import("~/blocks/acf/WhereToBuyBlock"));
const CoreButtonBlock = lazy(() => import("~/blocks/core/ButtonBlock"));
const CoreButtonsBlock = lazy(() => import("~/blocks/core/ButtonsBlock"));
const CoreCodeBlock = lazy(() => import("~/blocks/core/CodeBlock"));
const CoreColumnsBlock = lazy(() => import("~/blocks/core/ColumnsBlock"));
const CoreEmbedBlock = lazy(() => import("~/blocks/core/EmbedBlock"));
const CoreFileBlock = lazy(() => import("~/blocks/core/FileBlock"));
const CoreFreeformBlock = lazy(() => import("~/blocks/core/FreeformBlock"));
const CoreGroupBlock = lazy(() => import("~/blocks/core/GroupBlock"));
const CoreHeadingBlock = lazy(() => import("~/blocks/core/HeadingBlock"));
const CoreHtmlBlock = lazy(() => import("~/blocks/core/HtmlBlock"));
const CoreImageBlock = lazy(() => import("~/blocks/core/ImageBlock"));
const CoreListBlock = lazy(() => import("~/blocks/core/ListBlock"));
const CoreMediaTextBlock = lazy(() => import("~/blocks/core/MediaTextBlock"));
const CoreParagraphBlock = lazy(() => import("~/blocks/core/ParagraphBlock"));
const CorePullquoteBlock = lazy(() => import("~/blocks/core/PullquoteBlock"));
const CoreQuoteBlock = lazy(() => import("~/blocks/core/QuoteBlock"));
const CoreSeparatorBlock = lazy(() => import("~/blocks/core/SeparatorBlock"));
const CoreSpacerBlock = lazy(() => import("~/blocks/core/SpacerBlock"));
const CoreTableBlock = lazy(() => import("~/blocks/core/TableBlock"));

const GravityformsFormBlock = lazy(
    () => import("~/blocks/core/GravityformsFormBlock"),
);

const blocksOptions = {
    AcfAccordionsBlock,
    AcfAdBlock,
    AcfAdCardsGroupBlock,
    AcfAllApplicationsBlock,
    AcfAllCaseStudiesBlock,
    AcfAllEventsBlock,
    AcfAllFaqsBlock,
    AcfAllInsightsBlock,
    AcfAllNewsBlock,
    AcfCalculatorBlock,
    AcfCardLinksBlock,
    AcfContactBlock,
    AcfDocumentLibraryBlock,
    AcfDocumentsBlock,
    AcfFaqsBlock,
    AcfFeaturesBlock,
    AcfFilterAdBlock,
    AcfHeroBlock,
    AcfHeroCardsBlock,
    AcfHeroLogoButtonsBlock,
    AcfHubspotFormBlock,
    AcfIconLinksBlock,
    AcfIframeEmbedBlock,
    AcfImageCardsBlock,
    AcfImageTextBlock,
    AcfInformationBlock,
    AcfInsightsBlock,
    AcfJsEmbedBlock,
    AcfJumpLinksNavigationBlock,
    AcfLinksAdBlock,
    AcfLogosBlock,
    AcfPageHeaderBlock,
    AcfProductsCarouselBlock,
    AcfProductSupportBlock,
    AcfQrScannerBlock,
    AcfQuoteBlock,
    AcfQuoteSliderBlock,
    AcfRmaFormBlock,
    AcfSearchResultsBlock,
    AcfSiteSearchBlock,
    AcfTimelineBlock,
    AcfVideoEmbedBlock,
    AcfVideoLinksBlock,
    AcfWhereToBuyBlock,
    CoreButtonBlock,
    CoreButtonsBlock,
    CoreCodeBlock,
    CoreColumnsBlock,
    CoreEmbedBlock,
    CoreFileBlock,
    CoreFreeformBlock,
    CoreGroupBlock,
    CoreHeadingBlock,
    CoreHtmlBlock,
    CoreImageBlock,
    CoreListBlock,
    CoreMediaTextBlock,
    CoreParagraphBlock,
    CorePullquoteBlock,
    CoreQuoteBlock,
    CoreSeparatorBlock,
    CoreSpacerBlock,
    CoreTableBlock,
    GravityformsFormBlock,
};

const Block = ({ attrs, setBlockAsLoaded, name, id, ...props }) => {
    let TheBlock = null;

    const blockName = blockNameTransform(name);

    if (Object.keys(blocksOptions).includes(blockName)) {
        TheBlock = blocksOptions[blockName];
    }

    useEffect(() => {
        setBlockAsLoaded(id);
    }, [setBlockAsLoaded, id]);

    return TheBlock ? (
        <ErrorBoundary>
            <TheBlock {...{ attrs }} {...props} blockName={name} />
        </ErrorBoundary>
    ) : (
        <div>{`Unknown Block ${name}`}</div>
    );
};

Block.propTypes = {
    attrs: PropTypes.shape({}),
    name: PropTypes.string.isRequired,
};

const BlockList = ({
    blocks = [],
    languageCode = "en",
    setBlockAsLoaded = () => {},
}) => {
    return blocks && blocks.length ? (
        <Suspense fallback={<div>Loading</div>}>
            {blocks
                .filter((block) => block)
                .filter(({ name }) => name)
                .map((block, index) => (
                    <Block
                        {...block}
                        {...{ languageCode, setBlockAsLoaded }}
                        id={`${block.name}-${index}`}
                        key={`${block.name}-${index}`}
                    />
                ))}
        </Suspense>
    ) : (
        <div>No Blocks On Page</div>
    );
};

BlockList.propTypes = {
    blocks: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ),
    setBlockAsLoaded: PropTypes.func,
};

export default BlockList;
